import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  UserCog, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
  X
} from 'lucide-react';
import { db, collection, doc, setDoc, getDoc } from '../../firebase';
import styles from '../CSS/CreateUserForm.module.css';

const CreateUserForm = ({ roles, onCancel, onSuccess, showFeedback, editingUser = null, currentUser = null }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user'
  });
  
  const [selectedRoleData, setSelectedRoleData] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Permisos disponibles para mostrar información
  const availablePermissions = {
    'gestion': { name: 'Gestión', description: 'Acceso al módulo de gestión general', color: '#9f7aea' },
    'correos': { name: 'Gestión de Correos', description: 'Gestión de correos electrónicos', color: '#ed8936' },
    'clases': { name: 'Clases', description: 'Acceso al módulo de clases', color: '#38a169' },
    'reservas': { name: 'Reservas', description: 'Sistema de reservas', color: '#3182ce' },
    'incidencias': { name: 'Incidencias', description: 'Sistema de tickets/incidencias', color: '#e53e3e' },
    'admin': { name: 'Administración', description: 'Panel de administración completo', color: '#d69e2e' }
  };

  useEffect(() => {
    if (editingUser) {
      setFormData({
        email: editingUser.id,
        firstName: editingUser.firstName,
        lastName: editingUser.lastName,
        role: editingUser.role
      });
    }
  }, [editingUser]);

  useEffect(() => {
    if (formData.role) {
      loadRoleData(formData.role);
    }
  }, [formData.role]);

  const loadRoleData = async (roleId) => {
    if (roleId === 'admin') {
      setSelectedRoleData({
        name: 'Administrador',
        description: 'Acceso completo a todas las funcionalidades del sistema',
        permissions: Object.keys(availablePermissions)
      });
      return;
    }

    if (roleId === 'user') {
      setSelectedRoleData({
        name: 'Usuario',
        description: 'Acceso básico solo a Reservas e Incidencias (sin permisos avanzados)',
        permissions: ['reservas', 'incidencias']
      });
      return;
    }

    const role = roles.find(r => r.id === roleId);
    if (role) {
      setSelectedRoleData(role);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'El email no tiene un formato válido';
    }
    
    if (!formData.firstName) {
      errors.firstName = 'El nombre es requerido';
    }
    
    if (!formData.lastName) {
      errors.lastName = 'Los apellidos son requeridos';
    }
    
    if (!formData.role) {
      errors.role = 'Debe seleccionar un rol';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showFeedback("error", "Por favor, corrija los errores en el formulario");
      return;
    }

    // Validación especial para usuario actual
    if (editingUser && formData.email === currentUser?.email) {
      if (editingUser.role !== formData.role) {
        showFeedback("error", "No puedes cambiar tu propio rol");
        return;
      }
    }

    setLoading(true);
    
    try {
      const userDoc = doc(collection(db, "users"), formData.email);
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        ...(editingUser ? 
          { updatedAt: new Date().toISOString() } : 
          { createdAt: new Date().toISOString() }
        )
      };

      await setDoc(userDoc, userData, editingUser ? { merge: true } : {});
      onSuccess();
    } catch (error) {
      showFeedback("error", `Error al ${editingUser ? 'actualizar' : 'crear'} usuario: ` + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error cuando el usuario empiece a escribir
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isCurrentUser = editingUser && formData.email === currentUser?.email;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>
            {editingUser ? <UserCog size={24} /> : <User size={24} />}
            {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
            {isCurrentUser && (
              <span className={styles.currentUserBadge}>
                Tu cuenta
              </span>
            )}
          </h3>
          <p className={styles.subtitle}>
            {editingUser ? 
              'Modifica los datos del usuario seleccionado' : 
              'Completa los datos para crear un nuevo usuario en el sistema'
            }
          </p>
        </div>
        <button
          className={styles.closeButton}
          onClick={onCancel}
          title="Cerrar"
          disabled={loading}
        >
          <X size={20} />
        </button>
      </div>

      <div className={styles.form}>
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <Mail size={16} />
              Correo electrónico
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!!editingUser}
              placeholder="usuario@ejemplo.com"
              className={`${styles.input} ${validationErrors.email ? styles.inputError : ''} ${editingUser ? styles.inputDisabled : ''}`}
            />
            {validationErrors.email && (
              <span className={styles.error}>
                <AlertCircle size={14} />
                {validationErrors.email}
              </span>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <User size={16} />
              Nombre
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="Nombre"
              className={`${styles.input} ${validationErrors.firstName ? styles.inputError : ''}`}
            />
            {validationErrors.firstName && (
              <span className={styles.error}>
                <AlertCircle size={14} />
                {validationErrors.firstName}
              </span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <User size={16} />
              Apellidos
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Apellidos"
              className={`${styles.input} ${validationErrors.lastName ? styles.inputError : ''}`}
            />
            {validationErrors.lastName && (
              <span className={styles.error}>
                <AlertCircle size={14} />
                {validationErrors.lastName}
              </span>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            <UserCog size={16} />
            Rol del usuario
            {isCurrentUser && (
              <span className={styles.warningText}>
                (No puedes cambiar tu propio rol)
              </span>
            )}
          </label>
          <select
            value={formData.role}
            onChange={(e) => handleInputChange('role', e.target.value)}
            disabled={isCurrentUser}
            className={`${styles.select} ${validationErrors.role ? styles.inputError : ''} ${isCurrentUser ? styles.inputDisabled : ''}`}
          >
            <option value="">Seleccionar rol</option>
            <option value="admin">Administrador</option>
            <option value="user">Usuario</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </select>
          {validationErrors.role && (
            <span className={styles.error}>
              <AlertCircle size={14} />
              {validationErrors.role}
            </span>
          )}

          {selectedRoleData && (
            <div className={styles.roleInfo}>
              <div className={styles.roleHeader}>
                <div>
                  <div className={styles.roleName}>{selectedRoleData.name}</div>
                  <div className={styles.roleDescription}>{selectedRoleData.description}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPermissions(!showPermissions)}
                  className={styles.permissionsToggle}
                >
                  {showPermissions ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showPermissions ? 'Ocultar' : 'Ver'} permisos
                </button>
              </div>

              {showPermissions && (
                <div className={styles.permissionsList}>
                  {selectedRoleData.permissions?.map(permissionId => {
                    const permission = availablePermissions[permissionId];
                    return (
                      <div key={permissionId} className={styles.permissionItem}>
                        <div
                          className={styles.permissionDot}
                          style={{ backgroundColor: permission?.color || '#6b7280' }}
                        />
                        <span className={styles.permissionText}>
                          {permission?.name || permissionId}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={`${styles.button} ${styles.secondaryButton}`}
          disabled={loading}
        >
          <XCircle size={16} />
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className={`${styles.button} ${styles.primaryButton} ${loading ? styles.loadingButton : ''}`}
          disabled={loading}
        >
          {loading ? (
            <>Guardando...</>
          ) : (
            <>
              <CheckCircle size={16} />
              {editingUser ? 'Actualizar Usuario' : 'Crear Usuario'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CreateUserForm;